/**
 * Servico de API - Ordens de Servico (OS)
 * 
 * Este modulo gerencia todas as operacoes relacionadas as Ordens de Servico:
 * - Operacoes CRUD completas (criar, listar, obter, atualizar, deletar)
 * - Filtros e paginacao para listagem
 * - Tipos e interfaces TypeScript para type safety
 * - Constantes e helpers para exibicao
 * - Integracao com sistema de jobs/tasks ativos
 * 
 * Funcionalidades principais:
 * - Criacao de OS vinculadas a demandas
 * - Listagem com filtros por status, tipo, prioridade
 * - Busca e paginacao
 * - Atualizacao de status e informacoes
 * - Soft delete para manter historico
 * - Retomada de ultima OS do usuario
 * - Listagem de OS ativas em tempo real
 * 
 * @author Equipe GestaoeCRM
 * @date 2025-10-03
 * @version 0.16.6
 */

import api from './api';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

/**
 * Tipos de Ordem de Servico disponiveis no sistema
 * 
 * Cada tipo representa uma categoria de trabalho especifica:
 * - DESENVOLVIMENTO: Criacao de novas funcionalidades
 * - BUG_FIX: Correcao de problemas existentes
 * - MANUTENCAO: Manutencao preventiva ou corretiva
 * - TESTE: Testes de funcionalidades
 * - DEPLOY: Deploy de aplicacoes
 * - ANALISE: Analise de requisitos ou problemas
 * - REUNIAO: Reunioes e sessoes de trabalho
 * - DOCUMENTACAO: Criacao ou atualizacao de documentacao
 * - OUTRO: Outros tipos nao especificados
 */
export type OSType = 
  | 'DESENVOLVIMENTO'
  | 'BUG_FIX'
  | 'MANUTENCAO'
  | 'TESTE'
  | 'DEPLOY'
  | 'ANALISE'
  | 'REUNIAO'
  | 'DOCUMENTACAO'
  | 'OUTRO';

/**
 * Status possiveis de uma Ordem de Servico
 * 
 * Representa o estado atual da OS no fluxo de trabalho:
 * - PENDENTE: OS criada, aguardando inicio
 * - EM_ANDAMENTO: OS em execucao ativa
 * - PAUSADA: OS temporariamente pausada
 * - CONCLUIDA: OS finalizada com sucesso
 * - CANCELADA: OS cancelada ou abandonada
 */
export type OSStatus = 
  | 'PENDENTE'
  | 'EM_ANDAMENTO'
  | 'PAUSADA'
  | 'CONCLUIDA'
  | 'CANCELADA';

/**
 * Niveis de prioridade para Ordens de Servico
 * 
 * Define a urgencia e importancia da OS:
 * - BAIXA: Prioridade baixa, pode ser executada quando houver tempo
 * - MEDIA: Prioridade media, execucao normal
 * - ALTA: Prioridade alta, requer atencao especial
 * - CRITICA: Prioridade critica, maxima urgencia
 */
export type Priority = 'BAIXA' | 'MEDIA' | 'ALTA' | 'CRITICA';

/**
 * Interface principal para Ordem de Servico
 * 
 * Representa uma OS completa com todas as informacoes necessarias
 * para gerenciamento e exibicao no sistema.
 * 
 * Propriedades obrigatorias:
 * - id: Identificador unico da OS
 * - tenantId: ID do tenant (isolamento multi-tenant)
 * - userId: ID do usuario responsavel
 * - title: Titulo da OS
 * - type: Tipo da OS
 * - status: Status atual
 * - priority: Prioridade
 * - actualHours: Horas ja trabalhadas
 * - createdAt/updatedAt: Timestamps de controle
 * 
 * Propriedades opcionais:
 * - demandId/projectId: Vinculacao com demanda/projeto
 * - description: Descricao detalhada
 * - estimatedHours: Horas estimadas
 * - startDate/endDate: Datas de inicio/fim
 * - deletedAt: Soft delete
 * - demand: Dados da demanda vinculada
 * - sessions: Sessoes de trabalho
 */
export interface OrderOfService {
  /** ID unico da OS */
  id: string;
  /** ID do tenant (isolamento multi-tenant) */
  tenantId: string;
  /** ID da demanda vinculada (opcional) */
  demandId?: string;
  /** ID do projeto vinculado (opcional) */
  projectId?: string;
  /** ID do usuario responsavel */
  userId: string;
  /** Titulo da OS */
  title: string;
  /** Descricao detalhada (opcional) */
  description?: string;
  /** Tipo da OS */
  type: OSType;
  /** Status atual da OS */
  status: OSStatus;
  /** Prioridade da OS */
  priority: Priority;
  /** Tags para categorizacao */
  tags: string[];
  /** Horas estimadas (opcional) */
  estimatedHours?: number;
  /** Horas ja trabalhadas */
  actualHours: number;
  /** Marcação de urgente */
  isUrgent: boolean;
  /** Data de inicio (opcional) */
  startDate?: string;
  /** Data de fim (opcional) */
  endDate?: string;
  /** Data de criacao */
  createdAt: string;
  /** Data de ultima atualizacao */
  updatedAt: string;
  /** Data de exclusao (soft delete) */
  deletedAt?: string;
  /** Dados da demanda vinculada (opcional) */
  demand?: {
    id: string;
    title: string;
    status: string;
    priority?: string;
    manager?: {
      id: string;
      name: string;
      email: string;
    };
  };
  /** Dados do criador da OS (opcional) */
  user?: {
    id: string;
    name: string;
    email: string;
  };
  /** Dados da área da empresa associada (opcional) */
  companyArea?: {
    id: string;
    name: string;
    description?: string;
    active: boolean;
  } | null;
  /** Sessoes de trabalho (opcional) */
  sessions?: any[];
}

/**
 * Interface para dados de criacao de OS
 * 
 * Define os dados necessarios para criar um novo Job/Task.
 * Todos os campos sao opcionais exceto title, type e demandId.
 */
export interface CreateOSData {
  /** Titulo da OS (obrigatorio) */
  title: string;
  /** Descricao detalhada (opcional) */
  description?: string;
  /** Tipo da OS (obrigatorio) */
  type: OSType;
  /** Prioridade da OS (opcional, padrao: MEDIA) */
  priority?: Priority;
  /** ID da demanda vinculada (obrigatorio) */
  demandId: string;
  /** ID do projeto vinculado (opcional) */
  projectId?: string;
  /** Tags para categorizacao (opcional) */
  tags?: string[];
  /** Horas estimadas (opcional) */
  estimatedHours?: number;
  /** ID do responsavel pela OS (obrigatorio) */
  responsavel: string;
  /** ID da área da empresa (opcional) */
  companyAreaId?: string;
  /** Marcação de urgente (opcional, padrão: false) */
  isUrgent?: boolean;
}

/**
 * Interface para dados de atualizacao de OS
 * 
 * Define os campos que podem ser atualizados em uma OS existente.
 * Todos os campos sao opcionais.
 */
export interface UpdateOSData {
  /** Titulo da OS (opcional) */
  title?: string;
  /** Descricao detalhada (opcional) */
  description?: string;
  /** Tipo da OS (opcional) */
  type?: OSType;
  /** Status da OS (opcional) */
  status?: OSStatus;
  /** Prioridade da OS (opcional) */
  priority?: Priority;
  /** Tags para categorizacao (opcional) */
  tags?: string[];
  /** Horas estimadas (opcional) */
  estimatedHours?: number;
  /** ID da área da empresa (opcional) */
  companyAreaId?: string | null;
  /** Marcação de urgente (opcional) */
  isUrgent?: boolean;
}

/**
 * Interface para parametros de listagem de OS
 * 
 * Define os filtros e opcoes de paginacao para listar OS.
 * Todos os campos sao opcionais.
 */
export interface ListOSParams {
  /** Filtrar por status (opcional) */
  status?: OSStatus;
  /** Filtrar por tipo (opcional) */
  type?: OSType;
  /** Filtrar por prioridade (opcional) */
  priority?: Priority;
  /** Filtrar por projeto (opcional) */
  projectId?: string;
  /** Filtrar por demanda (opcional) */
  demandId?: string;
  /** Filtrar por área da empresa (opcional) */
  companyAreaId?: string;
  /** Busca por texto (opcional) */
  search?: string;
  /** Pagina atual (opcional, padrao: 1) */
  page?: number;
  /** Limite por pagina (opcional, padrao: 10) */
  limit?: number;
}

/**
 * Interface para resposta de listagem de OS
 * 
 * Contem os dados das OS e informacoes de paginacao.
 */
export interface ListOSResponse {
  /** Indica se a operacao foi bem-sucedida */
  success: boolean;
  /** Array de OS retornadas */
  data: OrderOfService[];
  /** Informacoes de paginacao */
  pagination: {
    /** Pagina atual */
    page: number;
    /** Limite por pagina */
    limit: number;
    /** Total de registros */
    total: number;
    /** Total de paginas */
    totalPages: number;
  };
}

// ============================================================================
// FUNCOES DA API
// ============================================================================

/**
 * Criar nova Ordem de Servico
 * 
 * Esta funcao cria um novo Job/Task vinculado a uma demanda especifica.
 * O Job/Task e criado com status PENDENTE e pode ter projeto opcional.
 * 
 * Processo:
 * 1. Valida dados de entrada
 * 2. Verifica se demanda existe e pertence ao tenant
 * 3. Calcula horas estimadas da demanda/projeto
 * 4. Cria OS no banco de dados
 * 5. Registra log de auditoria
 * 
 * @param data Dados da OS a ser criada
 * @returns Promise com a OS criada
 * @throws Error se dados invalidos ou demanda nao encontrada
 */
export async function createOS(data: CreateOSData): Promise<OrderOfService> {
  const response = await api.post<{ success: boolean; data: OrderOfService }>('/os', data);
  return response.data.data;
}

/**
 * Listar Ordens de Servico com filtros e paginacao
 * 
 * Esta funcao retorna uma lista paginada de OS com filtros opcionais.
 * Aplica isolamento multi-tenant e controle de visibilidade por role.
 * 
 * Filtros disponiveis:
 * - status: Filtrar por status da OS
 * - type: Filtrar por tipo da OS
 * - priority: Filtrar por prioridade
 * - projectId: Filtrar por projeto
 * - demandId: Filtrar por demanda
 * - search: Busca por texto no titulo/descricao
 * 
 * @param params Parametros de filtro e paginacao (opcional)
 * @returns Promise com lista paginada de OS
 */
export async function listOS(params?: ListOSParams): Promise<ListOSResponse> {
  const response = await api.get<ListOSResponse>('/os', { params });
  return response.data;
}

/**
 * Listar OS urgentes do usuário logado
 * 
 * Esta funcao retorna todas as OS marcadas como urgentes do usuário autenticado,
 * excluindo OS concluídas e canceladas.
 * 
 * Parametros opcionais:
 * - page: Número da página (default: 1)
 * - limit: Limite de registros por página (default: 10)
 * 
 * @param params Parametros de paginacao (opcional)
 * @returns Promise com lista paginada de OS urgentes
 * @throws Error se usuario nao autenticado
 */
export async function listUrgentOS(params?: {
  page?: number;
  limit?: number;
  onlyMine?: boolean;
}): Promise<ListOSResponse> {
  const queryParams: any = {};
  if (params?.page) queryParams.page = params.page;
  if (params?.limit) queryParams.limit = params.limit;
  if (params?.onlyMine !== undefined) queryParams.onlyMine = params.onlyMine.toString();
  const response = await api.get<ListOSResponse>('/os/urgent', { params: queryParams });
  return response.data;
}

/**
 * Obter detalhes de uma OS especifica
 * 
 * Esta funcao retorna os detalhes completos de uma OS especifica,
 * incluindo dados da demanda vinculada e sessoes de trabalho.
 * 
 * @param id ID da OS a ser obtida
 * @returns Promise com os detalhes da OS
 * @throws Error se OS nao encontrada ou sem permissao
 */
export async function getOSById(id: string): Promise<OrderOfService> {
  const response = await api.get<{ success: boolean; data: OrderOfService }>(`/os/${id}`);
  return response.data.data;
}

/**
 * Atualizar OS
 * 
 * Esta funcao atualiza os dados de uma OS existente.
 * Permite atualizar titulo, descricao, tipo, status, prioridade, etc.
 * 
 * @param id ID da OS a ser atualizada
 * @param data Dados a serem atualizados
 * @returns Promise com a OS atualizada
 * @throws Error se OS nao encontrada ou sem permissao
 */
export async function updateOS(id: string, data: UpdateOSData): Promise<OrderOfService> {
  const response = await api.put<{ success: boolean; data: OrderOfService }>(`/os/${id}`, data);
  return response.data.data;
}

/**
 * Deletar OS (soft delete)
 * 
 * Esta funcao marca uma OS como deletada (soft delete),
 * mantendo o historico no banco de dados.
 * 
 * @param id ID da OS a ser deletada
 * @returns Promise vazia
 * @throws Error se OS nao encontrada ou sem permissao
 */
export async function deleteOS(id: string): Promise<void> {
  await api.delete(`/os/${id}`);
}

/**
 * Retomar ultima OS do usuario
 * 
 * Esta funcao retorna a ultima OS que o usuario estava trabalhando,
 * facilitando a continuidade do trabalho.
 * 
 * @returns Promise com a ultima OS do usuario
 * @throws Error se nenhuma OS encontrada
 */
export async function resumeLastOS(): Promise<OrderOfService> {
  const response = await api.get<{ success: boolean; data: OrderOfService }>('/os/resume/last');
  return response.data.data;
}

// ============================================================================
// CONSTANTES E HELPERS
// ============================================================================

/**
 * Labels para tipos de OS
 * 
 * Mapeia os tipos de OS para labels em portugues
 * para exibicao na interface do usuario.
 */
export const OS_TYPE_LABELS: Record<OSType, string> = {
  DESENVOLVIMENTO: 'Desenvolvimento',
  BUG_FIX: 'Correção de Bug',
  MANUTENCAO: 'Manutenção',
  TESTE: 'Teste',
  DEPLOY: 'Deploy',
  ANALISE: 'Análise',
  REUNIAO: 'Reunião',
  DOCUMENTACAO: 'Documentação',
  OUTRO: 'Outro'
};

/**
 * Labels para status de OS
 * 
 * Mapeia os status de OS para labels em portugues
 * para exibicao na interface do usuario.
 */
export const OS_STATUS_LABELS: Record<OSStatus, string> = {
  PENDENTE: 'Pendente',
  EM_ANDAMENTO: 'Em Andamento',
  PAUSADA: 'Pausada',
  CONCLUIDA: 'Concluída',
  CANCELADA: 'Cancelada'
};

/**
 * Cores para status de OS
 * 
 * Mapeia os status de OS para cores do Tailwind CSS
 * para exibicao de badges e indicadores visuais.
 */
export const OS_STATUS_COLORS: Record<OSStatus, string> = {
  PENDENTE: 'gray',
  EM_ANDAMENTO: 'blue',
  PAUSADA: 'yellow',
  CONCLUIDA: 'green',
  CANCELADA: 'red'
};

/**
 * Labels para prioridades
 * 
 * Mapeia os niveis de prioridade para labels em portugues
 * para exibicao na interface do usuario.
 */
export const PRIORITY_LABELS: Record<Priority, string> = {
  BAIXA: 'Baixa',
  MEDIA: 'Média',
  ALTA: 'Alta',
  CRITICA: 'Crítica'
};

/**
 * Cores para prioridades
 * 
 * Mapeia os niveis de prioridade para cores do Tailwind CSS
 * para exibicao de badges e indicadores visuais.
 */
export const PRIORITY_COLORS: Record<Priority, string> = {
  BAIXA: 'green',
  MEDIA: 'yellow',
  ALTA: 'orange',
  CRITICA: 'red'
};

/**
 * Icones para tipos de OS (Lucide React)
 * 
 * Mapeia os tipos de OS para nomes de icones do Lucide React
 * para exibicao na interface do usuario.
 */
export const OS_TYPE_ICONS: Record<OSType, string> = {
  DESENVOLVIMENTO: 'Code',
  BUG_FIX: 'Bug',
  MANUTENCAO: 'Wrench',
  TESTE: 'TestTube',
  DEPLOY: 'Rocket',
  ANALISE: 'Search',
  REUNIAO: 'Users',
  DOCUMENTACAO: 'FileText',
  OUTRO: 'MoreHorizontal'
};

/**
 * Buscar todas as OS ativas da empresa (tenant)
 * 
 * Esta funcao retorna todas as OS que estao atualmente
 * em execucao (status EM_ANDAMENTO) no tenant do usuario.
 * 
 * Utilizada para:
 * - Dashboard administrativo
 * - Monitoramento de atividades
 * - Controle de recursos
 * 
 * @returns Promise com lista de OS ativas
 */
export const getActiveOS = async () => {
  const response = await api.get('/os/active');
  return response.data;
};

